import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createCommission,
  deleteCommission,
  getCommissions,
} from "../../service/commissionService";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import Toast from "../../components/common/toast/Toast";
import ToggleButton from "../../components/common/inputs/toggles/Toggle";
import { BasicCommission, ErrorValidation } from "../../types/types";
import DeleteButton from "../../components/common/buttons/DeleteButton";
import { PlusCircleIcon } from "lucide-react";
import AirPorts from "../../data/airports.json"; // Import airports data

const Commission: React.FC = () => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | "warning">(
    "success"
  );
  const [commissionsList, setCommissionsList] = useState<BasicCommission[]>([]);
  const [allAirLine, setAllAirLine] = useState<boolean>(false);
  const [allDestination, setAllDestination] = useState<boolean>(false);
  const [allDeparture, setAllDeparture] = useState<boolean>(false);

  // ✅ UPDATED: Initialize with tripType
  const [commission, setCommission] = useState<BasicCommission>({
    id: 0,
    departure: "",
    destination: "",
    airLine: "",
    value: "",
    isPercentage: false,
    tripType: "ALL",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    setIsDataLoading(true);
    const commissions = await getCommissions();
    setCommissionsList(commissions);
    setIsDataLoading(false);
  };

  // Add airport validation function
  const isValidAirportCode = (code: string): boolean => {
    if (code.toUpperCase() === "ALL") return true;
    return AirPorts.some(
      (airport) => airport.code.toUpperCase() === code.toUpperCase()
    );
  };

  const validateForm = (): ErrorValidation => {
    const { departure, destination, airLine, value, tripType } = commission;

    // ✅ UPDATED: Check for duplicate commission rule including Trip Type
    for (const existingCommission of commissionsList) {
      if (
        existingCommission.departure.toLowerCase().trim() ===
          departure.toLowerCase().trim() &&
        existingCommission.destination.toLowerCase().trim() ===
          destination.toLowerCase().trim() &&
        existingCommission.airLine.toLowerCase().trim() ===
          airLine.toLowerCase().trim() &&
        // Check if trip types match (or if strict overlap logic is needed)
        existingCommission.tripType === tripType
      ) {
        return {
          message:
            "Commission rule already exists for this route and trip type.",
          validate: false,
        };
      }
    }

    // Check if all fields are filled
    if (
      departure.trim() === "" ||
      destination.trim() === "" ||
      airLine.trim() === "" ||
      value.trim() === ""
    ) {
      return {
        message: "All fields are required.",
        validate: false,
      };
    }

    // Validate departure airport code
    if (!isValidAirportCode(departure.trim())) {
      return {
        message: `"${departure}" is not a valid airport code for departure.`,
        validate: false,
      };
    }

    // Validate destination airport code
    if (!isValidAirportCode(destination.trim())) {
      return {
        message: `"${destination}" is not a valid airport code for destination.`,
        validate: false,
      };
    }

    // Check that departure and destination are not the same (unless one is ALL)
    if (
      departure.toLowerCase().trim() === destination.toLowerCase().trim() &&
      departure.toLowerCase().trim() !== "all"
    ) {
      return {
        message: "Departure and destination cannot be the same.",
        validate: false,
      };
    }

    // UPDATED VALIDATION: Allow negative values
    const numValue = parseFloat(value);
    if (value.trim() === "" || isNaN(numValue)) {
      return {
        message: "Commission value must be a valid number.",
        validate: false,
      };
    }

    // Optional: Add range validation if needed
    if (commission.isPercentage && numValue < -100) {
      return {
        message: "Percentage discount cannot exceed -100%.",
        validate: false,
      };
    }

    return {
      message: "",
      validate: true,
    };
  };

  // ✅ UPDATED: Handler to support Select elements as well
  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setCommission((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAllDepartureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllDeparture(e.target.checked);
    if (!e.target.checked) {
      setCommission((prev) => ({
        ...prev,
        departure: "",
      }));
    } else {
      setCommission((prev) => ({
        ...prev,
        departure: "ALL",
      }));
    }
  };

  const handleAllAirLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllAirLine(e.target.checked);
    if (!e.target.checked) {
      setCommission((prev) => ({
        ...prev,
        airLine: "",
      }));
    } else {
      setCommission((prev) => ({
        ...prev,
        airLine: "ALL",
      }));
    }
  };

  const handleAllDestinationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAllDestination(e.target.checked);
    if (!e.target.checked) {
      setCommission((prev) => ({
        ...prev,
        destination: "",
      }));
    } else {
      setCommission((prev) => ({
        ...prev,
        destination: "ALL",
      }));
    }
  };

  const handleToggleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommission((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
    }));
  };

  const handleUpdate = async () => {
    const validateInfo = validateForm();
    if (validateInfo.validate === false) {
      setToastMessage(validateInfo.message);
      setShowToast(true);
      setToastType("error");
      return;
    }

    setIsDataLoading(true);

    const response = await createCommission(commission);

    setIsDataLoading(false);

    if (response?.success) {
      setCommissionsList((prev) => [...prev, { ...commission }]);

      // Reset the form
      setCommission({
        id: 0,
        departure: "",
        destination: "",
        airLine: "",
        value: "",
        isPercentage: false,
        tripType: "ALL", // Reset tripType
      });
      setAllAirLine(false);
      setAllDestination(false);
      setAllDeparture(false);

      setShowToast(true);
      setToastMessage("Commission added successfully!");
      setToastType("success");
    } else {
      setShowToast(true);
      setToastMessage(response?.message || "Failed to add commission");
      setToastType("error");
    }
  };

  const handleDelete = async (id: number) => {
    setIsDataLoading(true);
    const response = await deleteCommission(id);
    setCommissionsList((prev) =>
      prev.filter((commission) => commission.id !== id)
    );
    setIsDataLoading(false);
  };

  const handleAssignLuggage = async (commission: BasicCommission) => {
    navigate("/luggage", { state: commission });
  };

  // Helper function to format commission display
  const formatCommissionDisplay = (
    value: number | string,
    isPercentage: boolean
  ) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return value;

    if (numValue < 0) {
      return `${numValue}${isPercentage ? "%" : ""} discount`;
    } else if (numValue > 0) {
      return `+${numValue}${isPercentage ? "%" : ""}`;
    } else {
      return `0${isPercentage ? "%" : ""}`;
    }
  };

  // Helper to format Trip Type display
  const formatTripType = (type: string) => {
    switch (type) {
      case "ONEWAY":
        return "One Way";
      case "ROUNDTRIP":
        return "Round Trip";
      default:
        return "All";
    }
  };

  return (
    <div className="container mx-auto p-6">
      {showToast && (
        <Toast
          type={toastType}
          onClose={() => setShowToast(false)}
          message={toastMessage}
        />
      )}
      {isDataLoading && <LoadingIndicator></LoadingIndicator>}
      <h2 className="text-3xl font-bold mb-6">Commission Settings</h2>

      <div className="">
        <div className="">
          {/* --- ADD NEW COMMISSION TABLE --- */}
          <div className="overflow-x-auto bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Departure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Set All Departures
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destination
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Set All Destinations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Air Line
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-nowrap">
                    All Air Lines
                  </th>
                  {/* ✅ NEW HEADER */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trip Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={3}
                      type="text"
                      name="departure"
                      value={
                        allDeparture
                          ? "ALL"
                          : commission.departure.toUpperCase()
                      }
                      className="border border-gray-300 rounded p-2 w-14"
                      onChange={handleValueChange}
                      disabled={allDeparture}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      name="allDeparture"
                      value={allDeparture}
                      onValueChange={handleAllDepartureChange}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={3}
                      type="text"
                      name="destination"
                      value={
                        allDestination
                          ? "ALL"
                          : commission.destination.toUpperCase()
                      }
                      className="border border-gray-300 rounded p-2 w-14"
                      onChange={handleValueChange}
                      disabled={allDestination}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      name="allDestination"
                      value={allDestination}
                      onValueChange={handleAllDestinationChange}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={allAirLine ? 3 : 2}
                      type="text"
                      name="airLine"
                      value={
                        allAirLine ? "ALL" : commission.airLine.toUpperCase()
                      }
                      className="border border-gray-300 rounded p-2 w-14"
                      onChange={handleValueChange}
                      disabled={allAirLine}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      name="allAirLine"
                      value={allAirLine}
                      onValueChange={handleAllAirLineChange}
                    />
                  </td>

                  {/* ✅ NEW TRIP TYPE SELECTOR */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      name="tripType"
                      value={commission.tripType}
                      onChange={handleValueChange}
                      className="border border-gray-300 rounded p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="ALL">All Types</option>
                      <option value="ONEWAY">One Way</option>
                      <option value="ROUNDTRIP">Round Trip</option>
                    </select>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap flex items-center gap-1">
                    {commission.departure.toLowerCase() === "cmb"
                      ? "LKR"
                      : "EUR"}
                    <input
                      type="number"
                      name="value"
                      value={commission.value}
                      className="border border-gray-300 rounded p-2 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      onChange={handleValueChange}
                      placeholder=""
                      step="any"
                    />
                    <div className="font-bold text-blue-300">
                      {commission.isPercentage && "%"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      name="isPercentage"
                      value={commission.isPercentage}
                      onValueChange={handleToggleValueChange}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-right">
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-900`}
              onClick={handleUpdate}
            >
              Add
            </button>
          </div>

          {/* --- EXISTING COMMISSIONS LIST --- */}
          <div className="overflow-x-auto mt-6 bg-white shadow-md rounded-lg overflow-hidden mb-6">
            {commissionsList.length === 0 ? (
              <div className="p-2 flex justify-center text-sm text-gray-400">
                No commissions found.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Departure
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destination
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Air Line
                    </th>
                    {/* ✅ NEW HEADER */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trip Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Luggage offer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Settings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionsList.map((commission) => (
                    <tr key={commission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {commission.departure.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {commission.destination.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {commission.airLine.toUpperCase()}
                      </td>

                      {/* ✅ NEW COLUMN DATA */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            commission.tripType === "ONEWAY"
                              ? "bg-purple-100 text-purple-800"
                              : commission.tripType === "ROUNDTRIP"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {formatTripType(commission.tripType || "ALL")}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={
                            parseFloat(commission.value) < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }
                        >
                          {formatCommissionDisplay(
                            commission.value,
                            commission.isPercentage
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PlusCircleIcon
                          color="blue"
                          className="cursor-pointer "
                          onClick={() => {
                            handleAssignLuggage(commission);
                          }}
                        ></PlusCircleIcon>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DeleteButton
                          onConfirm={() => {
                            handleDelete(commission.id);
                          }}
                        ></DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commission;
